import { useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DEFAULT_PIPELINE, type HubPlanetDto, type IssueDto } from '@aiai/shared';
import { createIssue } from '../api';
import { qk } from '../api/queryKeys';
import { Modal } from './Modal';
import { errorMessage } from '../lib/api';
import { usePlanetsCanState } from '../hooks/usePlanetCapabilities';

/** The API's minimum brief length (trimmed). */
export const MIN_BRIEF_CHARS = 10;

interface Props {
  open: boolean;
  onClose: () => void;
  planets: readonly HubPlanetDto[];
  /** The planets list itself is still being fetched (an empty list is then not final). */
  loading?: boolean;
  onLaunched: (issue: IssueDto, planet: HubPlanetDto) => void;
}

/** File a new mission: "Plan it" (the PM plans first) or "Just build it" (approved straight away). */
export function LaunchMissionDialog({ open, onClose, planets, loading: planetsLoading = false, onLaunched }: Props) {
  const queryClient = useQueryClient();
  const { can, loading: grantsLoading } = usePlanetsCanState(planets);
  const eligible = planets.filter((p) => can(p, 'issue:create'));
  const [planetId, setPlanetId] = useState('');
  const [brief, setBrief] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const briefRef = useRef<HTMLTextAreaElement>(null);
  const returnTo = useRef<Element | null>(null);
  const fieldId = useId();

  const planet = eligible.find((p) => p.id === planetId) ?? eligible[0];
  const valid = brief.trim().length >= MIN_BRIEF_CHARS;

  // Focus moves into the dialog on open and returns to where it was on close.
  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement;
    setBrief('');
    setError(null);
    const t = window.setTimeout(() => briefRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(t);
      const el = returnTo.current;
      if (el instanceof HTMLElement && el.isConnected) el.focus();
    };
  }, [open]);

  const submit = async (build: boolean) => {
    // Synchronous guard: state updates are async, so a fast double click could
    // otherwise get two requests through before `pending` renders.
    if (inFlight.current || !planet || !valid) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const issue = await createIssue(
        { projectId: planet.id, body: brief.trim(), pipeline: [...DEFAULT_PIPELINE], ...(build ? { approved: true } : {}) },
        planet.workspace.slug,
      );
      void queryClient.invalidateQueries({ queryKey: qk.myOverview });
      onClose();
      onLaunched(issue, planet);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  const disabled = pending || !valid || !planet;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Launch mission"
      footer={
        <>
          <button type="button" className="btn-secondary" disabled={disabled} onClick={() => void submit(true)}>
            Just build it
          </button>
          <button type="button" className="btn-primary" disabled={disabled} onClick={() => void submit(false)}>
            Plan it
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {eligible.length === 0 ? (
          <p className="text-sm text-ink-muted">
            {planetsLoading || grantsLoading ? 'Loading…' : "You can't create issues on any planet yet."}
          </p>
        ) : (
          <>
            <div>
              <label htmlFor={`${fieldId}-planet`} className="label">
                Planet
              </label>
              <select
                id={`${fieldId}-planet`}
                className="input"
                value={planet?.id ?? ''}
                onChange={(e) => setPlanetId(e.target.value)}
              >
                {eligible.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.workspace.name})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`${fieldId}-brief`} className="label">
                Brief
              </label>
              <textarea
                id={`${fieldId}-brief`}
                ref={briefRef}
                className="input min-h-28"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="What should the team do? (at least 10 characters)"
              />
            </div>
          </>
        )}
        {error && (
          <p role="alert" className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
